import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useParams, Link } from 'react-router-dom';
import { OrderService, ListCustomerOrdersRequest, ListCustomerOrdersResponse, Order, OrderStatus } from '../api/api_pb';
import { useClient } from '../connect_api';
import RelativeTime from '../components/RelativeTime';
import { toast } from 'react-toastify';
import CurrencyFormatter from '../utils/currencyFormatter';
import { Form, Modal, Col, Row, Button } from 'react-bootstrap';
import { useGlobalContext } from '../GlobalContext';
import { formatAuthMethod } from '../utils/authMethodUtils';

const ShowLease: React.FC = () => {
    const { uuid } = useParams();
    const location = useLocation();
    const { findCustomerByUuid: findCustomerByMobileNumber } = useGlobalContext();
    const { state } = location;
    
    // Memoize customer to prevent infinite re-renders
    const customer = useMemo(() => {
        return state?.customer || findCustomerByMobileNumber(uuid!);
    }, [state?.customer, findCustomerByMobileNumber, uuid]);
    
    const orderClient = useClient(OrderService);
    const [orders, setOrders] = useState<Order[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
    const [isRefunding, setIsRefunding] = useState<boolean>(false);
    const [refundNote, setRefundNote] = useState<string>("")


    const fetchOrders = useCallback(async () => {
        if (!customer?.uuid) return;

        setIsLoading(true);
        try {
            const request = { customerUuid: customer.uuid } as ListCustomerOrdersRequest;
            const response = await orderClient.listCustomerOrders(request);
            const { orders: fetchedOrders } = response as ListCustomerOrdersResponse;
            setOrders(fetchedOrders);
        } catch (error) {
            console.error('Failed to fetch orders:', error);
            toast.dismiss();
            toast.error('Failed to fetch orders');
        } finally {
            setIsLoading(false);
        }

    }, [customer?.uuid, orderClient]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);


    const isRefundable = (order: Order) => {
        return !order.refundStatus?.refunded && order.paymentInformation?.rrn !== "";
    };

    const handleRefundClick = (order: Order) => {
        setSelectedOrder(order);
        setShowModal(true);
    };

    const handleRefundModalClose = () => {
        setShowModal(false)
        setRefundNote("")
        setSelectedOrder(null)
    }

    const handleNoteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setRefundNote(e.target.value);
    }


    const confirmRefund = () => {
        if (!selectedOrder || isRefunding) return;
        setIsRefunding(true);

        orderClient.refund({
            orderUuid: selectedOrder.uuid,
            refundNote: refundNote,
        }).then(() => {
            toast.dismiss();
            toast.success("The order has been successfully refunded")
            fetchOrders()
        }).catch((err: any) => {
            toast.dismiss();
            toast.error('Failed to refund order');
            console.log('error', err);
        }).finally(() => {
            setIsRefunding(false);
            setSelectedOrder(null);
            setShowModal(false);
        })
    };

    return (
        <div className="p-4">
            <table className="min-w-full bg-white border border-gray-200 mb-4">
                <thead>
                    <tr className="bg-gray-100">
                        <th className="border border-gray-300 text-left px-2 py-2">ID</th>
                        <th className="border border-gray-300 text-left px-2 py-2">Mobile</th>
                        <th className="border border-gray-300 text-left px-2 py-2">Created At</th>
                    </tr>
                </thead>
                <tbody>
                    {customer ? (
                        <tr>
                            <td className="border border-gray-300 px-2 py-2">{customer.uuid}</td>
                            <td className="border border-gray-300 px-2 py-2">
                                <div>{customer.mobileNumber}</div>
                                <div className="mt-1">
                                    <Link 
                                        to={`/customer/${customer.uuid}`}
                                        className="text-blue-600 hover:text-blue-800 underline text-sm"
                                    >
                                        Customer Detail
                                    </Link>
                                </div>
                            </td>
                            <td className="border border-gray-300 px-2 py-2">
                                {customer.createdAt ? (
                                    <div className="text-sm">
                                        <div><RelativeTime timestamp={customer.createdAt} /></div>
                                    </div>
                                ) : 'N/A'}
                            </td>
                        </tr>
                    ) : (
                        <tr>
                            <td colSpan={3} className="text-center py-4">No customer data available</td>
                        </tr>
                    )}
                </tbody>
            </table>

            <table className="min-w-full bg-white border border-gray-200">
                <thead>
                    <tr className="bg-gray-100">
                        <th className="border border-gray-300 text-left px-2 py-2">Created At</th>
                        <th className="border border-gray-300 text-left px-2 py-2">Order UUID</th>
                        <th className="border border-gray-300 text-left px-2 py-2">State</th>
                        <th className="border border-gray-300 text-left px-2 py-2">Auth Method</th>
                        <th className="border border-gray-300 text-left px-2 py-2">PaymentInformation</th>
                        <th className="border border-gray-300 text-left px-2 py-2">Product</th>
                        <th className="border border-gray-300 text-left px-2 py-2">Location </th>
                        <th className="border border-gray-300 text-left px-2 py-2">Dispensed At</th>
                        <th className="border border-gray-300 text-left px-2 py-2">Returned At</th>
                        <th className="border border-gray-300 text-left px-2 py-2">Paid At</th>
                        {/* <th className="border border-gray-300 text-left px-2 py-2">Action</th> */}
                    </tr>
                </thead>
                <tbody>
                    {isLoading ? (
                        <tr>
                            <td colSpan={10} className="text-center py-4">Loading...</td>
                        </tr>
                    ) : orders.length === 0 ? (
                        <tr>
                            <td colSpan={10} className="text-center py-4">No orders available</td>
                        </tr>
                    ) : (
                        orders.map((order) => (
                            <tr key={order.uuid}>
                                <td className="border border-gray-300 px-2 py-2">
                                    {order.createdAt ? (
                                        <div className="text-sm">
                                            <div><RelativeTime timestamp={order.createdAt} /></div>
                                        </div>
                                    ) : ''}
                                </td>
                                <td className="border border-gray-300 px-2 py-2">{order.uuid}</td>
                                <td className="border border-gray-300 px-2 py-2">
                                    {order.status !== undefined ? OrderStatus[order.status] : ''}
                                </td>
                                <td className="border border-gray-300 px-2 py-2">
                                    {formatAuthMethod(order.authMethod, order.venueAccessCode)}
                                </td>
                                <td className="border border-gray-300 px-2 py-2">
                                    {
                                        <>
                                            <div>Last 4:&nbsp;&nbsp;{order.paymentInformation?.last4}</div>
                                            {/* <div>aid:&nbsp;&nbsp; {order.paymentInformation?.aid}</div>
                                            <div>rrn:&nbsp;&nbsp; {order.paymentInformation?.rrn}</div> */}
                                            <div>Card Brand:&nbsp;&nbsp; {order.paymentInformation?.cardBrand}</div>
                                            {/* <div>applicationName:&nbsp;&nbsp; {order.paymentInformation?.applicationName}</div>
                                            <div>atc:&nbsp;&nbsp; {order.paymentInformation?.atc}</div> */}
                                            <div>
                                                {isRefundable(order) && (
                                                    <button
                                                        className="bg-red-500 text-white px-2 py-2 rounded-md shadow-md hover:bg-red-600"
                                                        onClick={() => handleRefundClick(order)}
                                                        disabled={isRefunding}
                                                    >
                                                        {isRefunding && selectedOrder?.uuid === order.uuid ? 'Processing...' : 'Refund'}
                                                    </button>
                                                )}
                                            </div>
                                        </>
                                    }
                                </td>
                                <td className="border border-gray-300 px-2 py-2">
                                    <div>Product: {order.productName || ''}</div>
                                    <div>────</div>
                                    <div>TrackableItem: {order.trackableItemName || ''}</div>
                                    <div>────</div>
                                    <div>Price: <CurrencyFormatter priceInCents={order.priceInCents} /></div>
                                </td>
                                <td className="border border-gray-300 px-2 py-2">
                                    <div>{order.machineName || ''}</div>
                                    <div>{order.doorIdDisplay || ''}</div>
                                </td>
                                <td className="border border-gray-300 px-2 py-2">
                                    {order.dispensedAt ? (
                                        <div className="text-sm">
                                            <div><RelativeTime timestamp={order.dispensedAt} /></div>
                                        </div>
                                    ) : ''}
                                </td>
                                <td className="border border-gray-300 px-2 py-2">
                                    {order.returnedAt ? (
                                        <div className="text-sm">
                                            <div><RelativeTime timestamp={order.returnedAt} /></div>
                                        </div>
                                    ) : ''}
                                </td>
                                <td className="border border-gray-300 px-2 py-2">
                                    {order.paidAt ? (
                                        <div className="text-sm">
                                            <div><RelativeTime timestamp={order.paidAt} /></div>
                                        </div>
                                    ) : ''}
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>

            <Modal show={showModal} onHide={handleRefundModalClose} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Refund</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group as={Row} controlId="formOrderUuid" className="mb-3">
                        <Form.Label column sm={4}>
                            Order Uuid
                        </Form.Label>
                        <Col sm={8}>
                            <Form.Control type="text" disabled={true} placeholder="" value={selectedOrder?.uuid || ''} />
                        </Col>
                    </Form.Group>
                    <Form.Group as={Row} controlId="formCustomerMobile" className="mb-3">
                        <Form.Label column sm={4}>
                            Customer
                        </Form.Label>
                        <Col sm={8}>
                            <Form.Control type="text" disabled={true} placeholder="" value={selectedOrder?.customer?.mobileNumber || ''} />
                        </Col>
                    </Form.Group>
                    <Form.Group as={Row} controlId="formRefundPrice" className="mb-3">
                        <Form.Label column sm={4}>
                            Refund Price In Cents
                        </Form.Label>
                        <Col sm={8}>
                            <Form.Control type="text" disabled={true} placeholder="" value={selectedOrder?.priceInCents || ''} />
                        </Col>
                    </Form.Group>

                    <Form.Group as={Row} controlId="formRefundNote" className="mb-3">
                        <Form.Label column sm={4}>
                            Refund Note
                        </Form.Label>
                        <Col sm={8}>
                            <Form.Control type="text" placeholder="Please enter the refund note" onChange={handleNoteChange} />
                        </Col>
                    </Form.Group>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={handleRefundModalClose}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={confirmRefund}>
                        Confirm
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ShowLease;
